/**
 * ENG-10134 (epic ENG-10127) — actively check whether the Mac's reverse tunnel
 * is alive, and make the MCP presence reaper act on the answer.
 *
 * WHY THIS EXISTS AT ALL. Claude Code does not reconnect a stdio MCP server.
 * When the tunnel drops, `ssh` notices via the keepalives baked into the
 * rendered entry (ServerAliveInterval/CountMax, computer-use-mcp.ts), exits,
 * and `computer-use-proxy` exits with it — so the child really does disappear
 * and the presence reaper (ENG-5053) really can see it. Only a respawn brings
 * the tools back; tmux persistence makes that worse rather than better, since a
 * long-lived session outlives tunnel blips and holds the dead state for as long
 * as it stays up.
 *
 * So the reaper is the respawn mechanism, and this module does not replace it.
 * It corrects the reaper's INPUT, for two reasons the reaper cannot fix itself.
 *
 * REASON 1 — THE REAPER CANNOT IDENTIFY THIS SERVER (ENG-10258).
 * `buildArgvMatchersForEntry` (stale-mcp-reaper.ts) derives a server's argv
 * signature by walking `entry.args` last-to-first and stopping at the first
 * package-spec-shaped token. Our args end `… -p 2222 brad@127.0.0.1 'open-
 * computer-use mcp'`: the remote command is skipped (it contains a space), the
 * walk reaches `brad@127.0.0.1`, strips the `@…` tail as if it were an npm
 * version suffix, and signs the server as **the Mac's SSH username**. One
 * sibling child with a `/home/<user>/` path in its argv then reads as this
 * server being alive — forever, silently, in the "everything looks fine"
 * direction this epic keeps running into. Measured against the real exported
 * matcher; see the test.
 *
 * ENG-10258 owns fixing that shared derivation for every integration. Here we
 * hand the reaper a view of the entry whose args are just the PROXY INVOCATION
 * (everything before the `--` separator), which is the part that identifies the
 * server. The same derivation then yields `computer-use-proxy.js` and matches
 * the right child. No shared code changes.
 *
 * REASON 2 — WITHOUT A TUNNEL CHECK, A SLEEPING MAC KILLS THE INTEGRATION DEAD.
 * Trace a Mac that is simply off. The proxy dies, the reaper restarts, the new
 * proxy's `ssh` is refused and dies inside the cold-start grace, `attempts`
 * increments; at 3 the key is given up. Give-up drops the key out of `active`,
 * so no further restart is spent — and `gaveUp` is cleared ONLY when the key is
 * seen live, which cannot happen without the respawn give-up just stopped.
 *
 * A managed toolkit escapes that through `onGiveUp` → mark-unhealthy → dropped
 * from `.mcp.json` → membership change → `clearPresenceReaperStateForKeys`.
 * `open-computer-use` is not a managed toolkit, has no `toolkit_id` mapping, and
 * so has no escape: three pointless respawns, then permanently dead until some
 * unrelated `.mcp.json` membership change happens by luck.
 *
 * Hence the gate. While the tunnel is unreachable the key is OMITTED from the
 * view the reaper sees. There is nothing to respawn into, so zero restarts are
 * spent — Mac-off thrash becomes structurally impossible rather than merely
 * rate-limited (AC#5). When the tunnel returns the key is exposed again, and any
 * give-up state accrued earlier is cleared by the caller, so a tunnel that comes
 * back always gets a fresh budget.
 */

import { connect, type Socket } from 'node:net';

import { COMPUTER_USE_MCP_KEY } from '@augmented/core/provisioning/computer-use-mcp.js';

import type { McpConfig } from './stale-mcp-reaper.js';

export { COMPUTER_USE_MCP_KEY };

/**
 * What the probe found. Deliberately separate from the verdict so the
 * classification is a pure function of an observation and can be asserted
 * without a socket.
 */
export type TunnelProbeOutcome =
  | { kind: 'banner'; line: string }
  | { kind: 'connected-no-banner' }
  | { kind: 'refused'; message: string }
  | { kind: 'timeout' }
  | { kind: 'error'; message: string };

/**
 * The operator-facing distinction AC#3 asks for.
 *
 * `reachable`        — the Mac's sshd answered through the tunnel.
 * `no-tunnel`        — nothing is listening. Mac off, asleep, autossh not
 *                      running, or the network is gone. These are genuinely
 *                      indistinguishable from the agent host, and saying so is
 *                      more useful than guessing between them.
 * `stale-forward`    — something accepted the connection but never sent an SSH
 *                      banner. A dead forward still holding the port: the case
 *                      `ExitOnForwardFailure=yes` exists to prevent on the Mac,
 *                      and that the agent host's sshd would clear sooner if it
 *                      set `ClientAliveInterval`/`ClientAliveCountMax` (it does
 *                      not today — see the runbook).
 * `container-vantage`— the agent is Docker-isolated, so its loopback is the
 *                      CONTAINER's, never the host's where the tunnel lands.
 *                      Not a tunnel fault and not probeable from here.
 *
 * WHAT THIS DELIBERATELY DOES NOT CLAIM: a LOCKED OR SLEEPING SCREEN is not a
 * transport failure. The tunnel is up, sshd answers, the proxy stays alive, and
 * the tools fail later at the accessibility layer. This mechanism therefore
 * reports `reachable` for a locked Mac and takes no action, which is correct —
 * a respawn would not help. The ticket lists "Mac asleep/locked" and
 * "tunnel down" as presenting identically to the user; they are told apart
 * here only to the extent that the transport can tell them apart, and a locked
 * screen is not one of the cases it can.
 */
export type TunnelState = 'reachable' | 'no-tunnel' | 'stale-forward' | 'container-vantage';

export interface TunnelVerdict {
  state: TunnelState;
  /** Operator-facing explanation. Never carries a credential. */
  detail: string;
  /** When the observation was made (ms epoch). */
  at: number;
}

/** A tunnel state other than `reachable` means "do not expose the key". */
export function isTunnelUsable(v: TunnelVerdict | null): boolean {
  // No verdict yet (first tick, or the probe has not returned) is NOT treated as
  // down. Withholding the key on no information would suppress MCP recovery,
  // and the pre-ENG-10134 behaviour — expose it, let the reaper decide — is the
  // safer default while we know nothing. The give-up deadlock this module
  // guards against is separately defused by the caller clearing reaper state on
  // a down→up transition, so an unreliable probe degrades to the status quo
  // rather than to a permanently dead integration.
  return v === null || v.state === 'reachable';
}

/** Pure: observation → verdict. */
export function classifyTunnelProbe(o: TunnelProbeOutcome, at: number): TunnelVerdict {
  switch (o.kind) {
    case 'banner':
      // Any SSH banner means a real sshd is on the other end of the forward.
      // We do not care which version; we care that the hop is complete.
      return o.line.startsWith('SSH-')
        ? { state: 'reachable', detail: `sshd answered through the tunnel (${o.line.trim()})`, at }
        : {
            state: 'stale-forward',
            detail:
              'the port accepted a connection and replied with something that is not an SSH ' +
              'banner — the forward is being held by something other than the Mac’s sshd',
            at,
          };
    case 'connected-no-banner':
      return {
        state: 'stale-forward',
        detail:
          'the port accepted a connection but sent no SSH banner — a dead forward is still ' +
          'holding it (the case ExitOnForwardFailure=yes prevents on the Mac side)',
        at,
      };
    case 'refused':
      return {
        state: 'no-tunnel',
        detail: `nothing is listening: ${o.message} — the Mac is off, asleep, or autossh is not running`,
        at,
      };
    case 'timeout':
      return {
        state: 'no-tunnel',
        detail:
          'the connection did not complete within its bound — the Mac is off, asleep, or ' +
          'unreachable',
        at,
      };
    case 'error':
      return { state: 'no-tunnel', detail: `probe could not connect: ${o.message}`, at };
  }
}

/** The loopback endpoint of the Mac's reverse tunnel, read off a rendered entry. */
export interface TunnelEndpoint {
  host: string;
  port: number;
}

/**
 * Pull the endpoint out of a rendered `open-computer-use` entry's args.
 *
 * Parsed rather than re-derived because the manager reads `.mcp.json` off disk;
 * the rendered args ARE what the agent's ssh will use, so parsing them cannot
 * disagree with reality the way a second derivation could. The shape it depends
 * on is pinned by a test that drives the real renderer, so a change to
 * computer-use-mcp.ts fails loudly here instead of silently returning null.
 *
 * The user token is matched on the SSH_USER shape (`computer-use-mcp.ts`'s
 * SAFE_SSH_USER) rather than by position, so a remote command containing `@`
 * cannot be mistaken for the destination.
 */
export function parseTunnelEndpoint(args: readonly string[] | undefined): TunnelEndpoint | null {
  if (!args || args.length === 0) return null;
  let port: number | null = null;
  for (let i = 0; i < args.length - 1; i++) {
    if (args[i] === '-p') {
      const n = Number(args[i + 1]);
      if (Number.isInteger(n) && n > 0 && n <= 65535) port = n;
      break;
    }
  }
  let host: string | null = null;
  for (const a of args) {
    const m = /^[A-Za-z0-9][A-Za-z0-9._-]*@(.+)$/.exec(a ?? '');
    if (m?.[1]) {
      host = m[1];
      break;
    }
  }
  if (port === null || host === null) return null;
  return { host, port };
}

/**
 * Most bytes we will buffer waiting for a banner's newline.
 *
 * RFC 4253 §4.2 caps the SSH identification string at 255 bytes including CRLF,
 * so anything longer is not a slow banner — it is a different protocol on the
 * port, which is exactly the stale-forward case.
 */
export const MAX_BANNER_BYTES = 255;

/** How long to wait for the TCP connect and then for the banner. */
export const DEFAULT_PROBE_TIMEOUT_MS = 3_000;

/**
 * Actively check the tunnel: connect to the loopback forward and wait for the
 * SSH banner. This is the "actively checked, rather than inferred from the
 * absence of an error" half of AC#1 — an sshd that answers is positive evidence,
 * where a quiet `.mcp.json` is not evidence of anything.
 *
 * Never throws and never rejects; every failure becomes an outcome.
 */
export function probeTunnel(
  endpoint: TunnelEndpoint,
  opts: { timeoutMs?: number } = {},
): Promise<TunnelProbeOutcome> {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_PROBE_TIMEOUT_MS;
  return new Promise((resolve) => {
    let settled = false;
    let connected = false;
    let socket: Socket | null = null;
    // Bytes seen so far, pending a newline. Held as a Buffer rather than a
    // string so a multi-byte sequence split across segments cannot be corrupted
    // by decoding each chunk on its own.
    let banner: Buffer | null = null;
    const done = (o: TunnelProbeOutcome) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        socket?.destroy();
      } catch {
        // Best-effort teardown; the outcome is already decided.
      }
      resolve(o);
    };
    // One timer covers connect AND banner. A connection that opens and then says
    // nothing is the stale-forward case, so the timeout's meaning depends on
    // whether we got that far.
    const timer = setTimeout(() => {
      done(connected ? { kind: 'connected-no-banner' } : { kind: 'timeout' });
    }, timeoutMs);
    try {
      socket = connect({ host: endpoint.host, port: endpoint.port });
    } catch (err) {
      done({ kind: 'error', message: (err as Error).message });
      return;
    }
    socket.on('connect', () => {
      connected = true;
    });
    // ACCUMULATE. `once('data')` reads whichever bytes happened to arrive in the
    // first TCP segment, and nothing guarantees that is the whole banner — a
    // sshd whose greeting is split across two writes would deliver `SS`, fail
    // `startsWith('SSH-')`, and be classified as a stale forward. That is the
    // false-negative direction: a healthy tunnel reported dead, which withholds
    // the key from the reaper and suppresses recovery.
    socket.on('data', (chunk: Buffer) => {
      banner = banner ? Buffer.concat([banner, chunk]) : chunk;
      const text = banner.toString('utf8');
      const nl = text.indexOf('\n');
      if (nl !== -1) {
        done({ kind: 'banner', line: text.slice(0, nl) });
        return;
      }
      // RFC 4253 §4.2 caps the identification string at 255 bytes including
      // CRLF, so bytes past that with no newline are not a banner being slow —
      // they are something else on the port, which is the stale-forward tell.
      if (banner.length > MAX_BANNER_BYTES) {
        done({ kind: 'banner', line: text.slice(0, MAX_BANNER_BYTES) });
      }
    });
    socket.on('error', (err: NodeJS.ErrnoException) => {
      // ECONNREFUSED is the ordinary "Mac is off" answer, not an anomaly.
      done(
        err.code === 'ECONNREFUSED'
          ? { kind: 'refused', message: `connection refused on ${endpoint.host}:${endpoint.port}` }
          : { kind: 'error', message: err.message },
      );
    });
    socket.on('close', () => {
      // Closed with a partial, newline-less greeting. A prefix of `SSH-` is not
      // evidence either way — a real sshd does not hang up mid-banner — so this
      // reports what is true: something accepted the connection and never
      // completed a banner.
      done(
        connected
          ? { kind: 'connected-no-banner' }
          : { kind: 'refused', message: `closed without connecting on ${endpoint.host}:${endpoint.port}` },
      );
    });
  });
}

/**
 * Narrow the computer-use entry's args to the proxy invocation, so the shared
 * argv-signature derivation identifies THIS server.
 *
 * Split out from {@link computerUseReaperView} because the signature fix and the
 * tunnel gate answer different questions and not every caller wants both. The
 * `/host/session-tool-bind` probe (session-tool-bind-probe-host.ts) runs the
 * same matcher to decide whether a declared server is BOUND, and it inherits the
 * identical mis-identification: a dead computer-use child reads as present and
 * is reported bound to the control plane — false-healthy, and reported upward.
 * It wants this correction and NOT the gate: while the tunnel is down the server
 * genuinely is unbound, and saying so is the accurate answer.
 *
 * Pure; returns the SAME object when there is nothing to do, so a tree with no
 * computer-use install is untouched down to the allocation.
 */
export function computerUseSignatureView(mcpJson: McpConfig | null): McpConfig | null {
  if (!mcpJson) return mcpJson;
  const servers = mcpJson.mcpServers;
  if (!servers || !Object.prototype.hasOwnProperty.call(servers, COMPUTER_USE_MCP_KEY)) {
    return mcpJson;
  }
  const entry = servers[COMPUTER_USE_MCP_KEY];
  const args = entry?.args;
  if (!Array.isArray(args) || args.length === 0) return mcpJson;
  // Everything before `--` is the proxy invocation — the part that identifies
  // this server. After it is the ssh command line, whose trailing tokens are
  // what mislead the shared signature derivation (ENG-10258).
  const sep = args.indexOf('--');
  const head = sep > 0 ? args.slice(0, sep) : [args[0]!];
  return {
    ...mcpJson,
    mcpServers: { ...servers, [COMPUTER_USE_MCP_KEY]: { ...entry, args: head } },
  };
}

/**
 * Rewrite the `.mcp.json` the presence reaper is given so it can act correctly
 * on this one server: the signature fix above, plus the tunnel gate.
 *
 * Pure; returns the SAME object when there is nothing to do.
 */
export function computerUseReaperView(
  mcpJson: McpConfig | null,
  verdict: TunnelVerdict | null,
): McpConfig | null {
  if (!mcpJson) return mcpJson;
  const servers = mcpJson.mcpServers;
  if (!servers || !Object.prototype.hasOwnProperty.call(servers, COMPUTER_USE_MCP_KEY)) {
    return mcpJson;
  }
  if (!isTunnelUsable(verdict)) {
    // Omit the key entirely: the reaper sees nothing to recover, spends no
    // restart, and accrues no give-up budget against a Mac that is simply off.
    const { [COMPUTER_USE_MCP_KEY]: _omitted, ...rest } = servers;
    return { ...mcpJson, mcpServers: rest };
  }
  return computerUseSignatureView(mcpJson);
}

/**
 * Minimum spacing between probes for one agent.
 *
 * The probe is a real TCP connection to the Mac's sshd, which writes a
 * `[preauth]` line to the operator's own log every time. Spacing it independently
 * of the manager's poll rate keeps that noise proportional to incidents rather
 * than to uptime, and a tunnel that is down for less than this window needs no
 * action anyway — the session is dead until a respawn either way.
 */
export const PROBE_MIN_INTERVAL_MS = 60_000;

export interface TunnelWatcherDeps {
  now?: () => number;
  probe?: (endpoint: TunnelEndpoint) => Promise<TunnelProbeOutcome>;
  minIntervalMs?: number;
}

/**
 * Per-observation hooks.
 *
 * These are supplied per call rather than at construction because the manager's
 * `log` is function-scoped while the watcher is a module-level singleton whose
 * cache must outlive any single poll. Passing them in also keeps the watcher
 * free of manager imports, so its tests need no manager at all.
 */
export interface TunnelObserveHooks {
  log?: (msg: string) => void;
  /**
   * Called when an agent's tunnel transitions from unusable back to reachable.
   *
   * The caller uses this to clear the presence reaper's give-up state for this
   * key. It is the belt to the gate's braces: while the gate holds, no give-up
   * budget is spent at all, but if the probe were ever wrong or unavailable the
   * key could still reach the permanent give-up described at the top of this
   * file. Clearing on recovery means a tunnel that comes back always gets a
   * fresh restart budget, whatever happened while it was away.
   */
  onRecovered?: (codeName: string) => void;
}

interface WatchEntry {
  verdict: TunnelVerdict | null;
  probing: boolean;
  loggedState: TunnelState | null;
}

/**
 * Per-agent tunnel liveness, refreshed OUT OF BAND.
 *
 * `observe()` is synchronous and returns whatever is currently known, so the
 * manager's poll never waits on a socket; it starts a probe when the cached
 * verdict is stale and the next poll reads the result. The first observation
 * therefore returns null, which `isTunnelUsable` treats as "no information" and
 * so leaves pre-ENG-10134 behaviour intact for exactly one tick.
 */
export class TunnelWatcher {
  private readonly entries = new Map<string, WatchEntry>();
  private readonly now: () => number;
  private readonly probeFn: (e: TunnelEndpoint) => Promise<TunnelProbeOutcome>;
  private readonly minIntervalMs: number;

  constructor(deps: TunnelWatcherDeps = {}) {
    this.now = deps.now ?? Date.now;
    this.probeFn = deps.probe ?? ((e) => probeTunnel(e));
    this.minIntervalMs = deps.minIntervalMs ?? PROBE_MIN_INTERVAL_MS;
  }

  verdictFor(codeName: string): TunnelVerdict | null {
    return this.entries.get(codeName)?.verdict ?? null;
  }

  /** Drop an agent's state (deprovision / rename). */
  forget(codeName: string): void {
    this.entries.delete(codeName);
  }

  /**
   * Read the current verdict and, if it is stale, start a refresh.
   *
   * `isolated` short-circuits the probe entirely. A Docker-isolated agent's
   * `127.0.0.1` is the CONTAINER's loopback — the manager provisions either a
   * per-agent `--internal` network (egress mode) or the default bridge, and
   * neither shares the host's loopback where the Mac's reverse tunnel lands. So
   * the in-container ssh can never reach the tunnel, and a probe run from the
   * host would answer for an endpoint the agent cannot use: it would say
   * "reachable", expose the key, and the reaper would respawn into guaranteed
   * failure every poll — precisely the thrash AC#5 forbids. The verdict is
   * derived from that topology rather than measured, because it is a property of
   * how the container is created, not of the Mac.
   */
  observe(
    codeName: string,
    args: readonly string[] | undefined,
    isolated: boolean,
    hooks: TunnelObserveHooks = {},
  ): TunnelVerdict | null {
    const at = this.now();
    if (isolated) {
      return this.record(
        codeName,
        {
          state: 'container-vantage',
          detail:
            'agent is Docker-isolated; the container’s loopback is not the host’s, so the ' +
            'Mac’s reverse tunnel is unreachable from inside it — computer-use cannot work for ' +
            'this agent',
          at,
        },
        hooks,
      );
    }

    const endpoint = parseTunnelEndpoint(args);
    if (!endpoint) {
      // Unparseable args are not evidence the tunnel is down. Report no
      // information so the key stays exposed and behaviour is unchanged.
      return this.verdictFor(codeName);
    }

    const cur = this.entries.get(codeName);
    const fresh = cur?.verdict != null && at - cur.verdict.at < this.minIntervalMs;
    if (!cur?.probing && !fresh) {
      this.startProbe(codeName, endpoint, hooks);
    }
    return cur?.verdict ?? null;
  }

  private startProbe(
    codeName: string,
    endpoint: TunnelEndpoint,
    hooks: TunnelObserveHooks,
  ): void {
    const prev = this.entries.get(codeName);
    this.entries.set(codeName, {
      verdict: prev?.verdict ?? null,
      probing: true,
      loggedState: prev?.loggedState ?? null,
    });
    // Fire-and-forget by design: the poll must not wait on a socket, and a
    // probe that never settles must not wedge the manager. probeTunnel resolves
    // every path, but the catch is here so a future change cannot make an
    // unhandled rejection out of a liveness check.
    void this.probeFn(endpoint)
      .then((outcome) => {
        this.record(codeName, classifyTunnelProbe(outcome, this.now()), hooks);
      })
      .catch((err: unknown) => {
        this.record(
          codeName,
          { state: 'no-tunnel', detail: `probe threw: ${(err as Error).message}`, at: this.now() },
          hooks,
        );
      })
      .finally(() => {
        const e = this.entries.get(codeName);
        if (e) e.probing = false;
      });
  }

  private record(
    codeName: string,
    verdict: TunnelVerdict,
    hooks: TunnelObserveHooks,
  ): TunnelVerdict {
    const prev = this.entries.get(codeName);
    // "Was usable" requires a PRIOR verdict: a first observation of `reachable`
    // is not a recovery, and treating it as one would clear reaper state on
    // every manager restart.
    const hadVerdict = prev?.verdict != null;
    const wasUsable = hadVerdict && prev!.verdict!.state === 'reachable';
    this.entries.set(codeName, {
      verdict,
      probing: prev?.probing ?? false,
      loggedState: prev?.loggedState ?? null,
    });
    // Log on state CHANGE only — this runs about once a minute per computer-use
    // agent, and a per-probe line would bury the transition that matters.
    if (prev?.loggedState !== verdict.state) {
      this.entries.get(codeName)!.loggedState = verdict.state;
      hooks.log?.(`[computer-use-tunnel] '${codeName}': ${verdict.state} — ${verdict.detail}`);
    }
    if (hadVerdict && !wasUsable && verdict.state === 'reachable') {
      // Swallowed deliberately, mirroring how the reaper wraps every callback it
      // fires. `record` runs inside the probe's promise chain, so a throwing hook
      // would otherwise be caught by the chain's own .catch and RECORDED AS
      // `no-tunnel: probe threw` — turning a successful probe into a down verdict
      // and closing the gate on a healthy tunnel.
      try {
        hooks.onRecovered?.(codeName);
      } catch (err) {
        hooks.log?.(
          `[computer-use-tunnel] '${codeName}': onRecovered threw (suppressed; verdict stands): ${(err as Error).message}`,
        );
      }
    }
    return verdict;
  }
}
